import {Injectable} from '@angular/core';
import {DbModalService} from "../../modules/modal/modal";
import {CreateWebsiteComponent} from "../../components/common/create-website/create-website.component";
import {UserSubscription, UserSubscriptionCategory, UserSubscriptionType} from "../../user/user-subscription";
import {Store} from "@ngrx/store";
import * as fromStore from '../../store';
import {filter, switchMap} from "rxjs/operators";
import {
  LimitExceededComponent,
  LimitExceededMsgType
} from "../../components/common/limit-exceeded/limit-exceeded.component";
import {IWorkspaceIdEntities} from "../../store/reducers/workspace.reducer";
import {clientRoles} from "../../user/user-roles-actions";
import {
  DownloadManagerConnectComponent
} from "../../components/common/download-manager-connect/download-manager-connect.component";
import {
  DownloadManagerRecreateComponent
} from "../../components/common/download-manager-recreate/download-manager-recreate.component";
import {MigrateWebsiteComponent} from "../../components/common/migrate-website/migrate-website.component";
import {MigrationDialogComponent} from "../../shared/components/dialogs/migration/migration-dialog.component";
import {DialogService} from "../../../ng2-bootstrap-modal/dialog.service";
import {
  BoosterPluginDownloadDialogComponent
} from "../../websites/website/booster/dialogs/booster-plugin-download-dialog/booster-plugin-download-dialog.component";
import {UpgradeTenWebProComponent} from "../../components/common/upgrade-ten-web-pro/upgrade-ten-web-pro.component";
import {Config} from "../../config/config";
import {GoogleAnalyticsService} from "../../shared/services/google-analytics/google-analytics.service";
import {CloneWebsiteComponent} from "../../components/common/clone-website/clone-website.component";
import {Website} from "../../websites/website/website";
import {combineLatest} from "rxjs";
import {User} from "../../user/user";
import {
  ManualMigrationDialogComponent
} from "../../shared/components/dialogs/migration/manual-migration-dialog/manual-migration-dialog.component";
import {CloningProgressComponent} from "../../components/common/clone-website-progress/clone-progress.component";
import {
  DedicatedVmContactComponent
} from "../../components/common/dedicated-vm-contact-dialiog/dedicated-vm-contact.component";
import {TypeUtils} from "../../shared/utils/type-utils";
import {
  OneWeekFreeDemoUpgradeComponent
} from "../../shared/components/dialogs/one-month-free/components/one-week-free-demo-upgrade/one-week-free-demo-upgrade.component";
import {Utils} from "../../shared/utils/utils";
import { ApiResellerProComponent } from '../../shared/components/dialogs/api-reseller-pro/api-reseller-pro.component';
import {
  PricingPageViewComponent
} from "../../shared/components/dialogs/one-month-free/components/pricing-page-view/pricing-page-view.component";
import {CreateWebsiteTrialComponent} from "../../components/common/create-website-trial/create-website-trial.component";

@Injectable({
  providedIn: 'root'
})
export class CreateWebsiteService {

  private _userSubscription: UserSubscription;
  private _user: User;
  private _canCreate = false;
  private _canConnect = false;
  private _canConnectSpeed = false;
  private _workspaceEntities: IWorkspaceIdEntities;
  private _hostedWebsitesCount: any;
  private _connectedWebsitesCount: any;
  public websites: any;
  demoUser: boolean;

  constructor(
    private readonly _modalService: DbModalService,
    private readonly _store: Store<fromStore.DbStoreState>,
    private readonly _dialogService: DialogService,
    private readonly _googleAnalyticsService: GoogleAnalyticsService,
  ) {
    this._getUserSubscription();
    this._getWorkspaceEntities();
    this._getWebsites();
  }

  private _getUserSubscription(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter(loaded => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getUser),
          this._store.select(fromStore.getUserSubscription)
        )),
      )
      .subscribe(([user, userSubscription]) => {
        this.demoUser = Utils.isDemoUser(userSubscription, user);
        this._user = user;
        this._userSubscription = userSubscription;
        if (!userSubscription) return;
        this._canCreate = userSubscription.domainsPermission && userSubscription.domainsPermission.canCreateWebsite;
        this._canConnect = userSubscription.domainsPermission && userSubscription.domainsPermission.canConnectWebsite;
        this._canConnectSpeed = userSubscription.domainsPermission && userSubscription.domainsPermission.canConnectSpeedWebsite;
      });
  }

  private _getWebsites(): void {
    this._store.select(fromStore.getWebsites)
      .pipe(
        filter(websites => !!websites)
      )
      .subscribe(websites => {
        this.websites = websites.length;
        this._hostedWebsitesCount = websites.filter(website => !website.parentId && website.isActive && website.type === 'live' && !!website.domain_hash).length;
        this._connectedWebsitesCount = websites.filter(website => !website.parentId && website.isActive && website.type === 'live' && website.connectedFrom === 'speed_optimizer').length;
      });
  }

  private _getWorkspaceEntities(): void {
    this._store.select(fromStore.getWorkspaceIdEntities)
      .pipe(
        filter(entities => !!entities)
      )
      .subscribe(entities => this._workspaceEntities = entities);
  }

  public openCreateWebsiteModal(fromCreateMigrate, label?): void {
      if (this._userSubscription && this._userSubscription.trialBoosterClient) {
          return this.openProPlansUpgradeModal();
      }
      if (!this._canCreate || (this._userSubscription && this._userSubscription.boosterProClient
          && (this._hostedWebsitesCount === this._userSubscription.plan.additionalDomainCount))) {
          if (this._userSubscription && this._userSubscription.subscriptionId === Config.defaultStarterPlanId) {
              return this.showOneMonthFreeModal(label);
          }

          if (this._userSubscription && this._userSubscription.isDedicatedVM) {
              this._modalService.open(DedicatedVmContactComponent, {hasCloseBtn: true, height: 208, width: 500});
              return;
          }
          this.openLimitExceededModal(fromCreateMigrate);
          return;
      }

      if (this._userSubscription && this._userSubscription.type === UserSubscriptionType.FREE) {
          this._modalService.open(CreateWebsiteTrialComponent, {backdrop: 'static'});
      } else {
          const modal = this._modalService.open(CreateWebsiteComponent, {backdrop: 'static'});
          modal.componentInstance.userSubscription = this._userSubscription;
      }
  }

  public openCloneWebsiteModal(website: Website): void {
    if (!this._canCreate) {
      if (this._userSubscription && this._userSubscription.isDedicatedVM) {
        this._modalService.open(DedicatedVmContactComponent, {hasCloseBtn: true, height: 208, width: 500});
      } else {
        this.openLimitExceededModal();
      }
      return;
    }
    const modal = this._modalService.open(CloneWebsiteComponent, {backdrop: 'static'});
    modal.componentInstance.websiteIds = website.siblingId ? website.siblingId : {live: website.id, dev: null};
    modal.componentInstance.originalDomain = website.site_url;
    modal.componentInstance.hostedWebsitesLimit = +this._userSubscription.plan.services.hosting.domain_count.parameter_value + this._userSubscription.plan.additionalDomainCount;
    modal.componentInstance.userSubscription = this._userSubscription;
  }

  public openMigrateWebsiteModal(websiteId: number, websiteUrl: string, migrationProcess = false, zipUploading = false, isClone: boolean = false): void {
    if (!migrationProcess) {
      if (this._userSubscription && this._userSubscription.trialBoosterClient) {
        return this.openProPlansUpgradeModal();
      }
      if (!this._canCreate || (this._userSubscription && this._userSubscription.boosterProClient
        && (this._hostedWebsitesCount === this._userSubscription.plan.additionalDomainCount))) {
        if (this._userSubscription && this._userSubscription.subscriptionId === Config.defaultStarterPlanId) {
          return this.showOneMonthFreeModal('Dashboard: Websites list: Migrate button');
        }

        if (this._userSubscription && this._userSubscription.isDedicatedVM) {
          this._modalService.open(DedicatedVmContactComponent, {hasCloseBtn: true, height: 208, width: 500});
          return;
        }

        this.openLimitExceededModal();
        return;
      }
    }
     if (!!isClone) {
       const modal = this._modalService.open(CloningProgressComponent,  {height: 174, width: 700});
       modal.componentInstance.fromNotifs = true;
     } else {
       const modal = this._modalService.open(MigrateWebsiteComponent, {backdrop: 'static'});
       modal.componentInstance.websiteId = websiteId;
       modal.componentInstance.migrationProcess = migrationProcess;
       modal.componentInstance.websiteUrl = websiteUrl;
       modal.componentInstance.zipUploading = zipUploading;
     }

  }

  public openDownloadManagerMigrateModal(fromCreateMigrate, manualMigration = false, label?): void {
    if (this._userSubscription && this._userSubscription.trialBoosterClient) {
      return this.openProPlansUpgradeModal();
    }
    if (!this._canCreate || (this._userSubscription && this._userSubscription.boosterProClient
      && (this._hostedWebsitesCount === this._userSubscription.plan.additionalDomainCount))) {
      if (this._userSubscription && this._userSubscription.subscriptionId === Config.defaultStarterPlanId) {
        return this.showOneMonthFreeModal(label);
      }
      if (this._userSubscription && this._userSubscription.isDedicatedVM) {
        this._modalService.open(DedicatedVmContactComponent, {hasCloseBtn: true, height: 208, width: 500});
        return;
      }
      this.openLimitExceededModal(fromCreateMigrate);
      return;
    }
    if (manualMigration) {
      this._dialogService.addDialog(ManualMigrationDialogComponent);
      return;
    }
    this._dialogService.addDialog(MigrationDialogComponent);
  }

  public openOptimizeModal(): void {
    const analyticsData = {
      eventCategory: 'Dashboard Action',
      eventAction: 'Optimize more websites - card',
      eventLabel: 'Speed Booster'
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);

    if (!this._canConnectSpeed) {
      if (this._userSubscription && this._userSubscription.subscriptionId === Config.defaultStarterPlanId) {
        return this.showOneMonthFreeModal('Dashboard: Websites list: Optimize button');
      }
      this.openLimitExceededModal();
      return;
    }
    this._modalService.open(BoosterPluginDownloadDialogComponent, {height: 550, width: 940});
  }

  public openDownloadManagerRecreateModal(): void {
    this._modalService.open(DownloadManagerRecreateComponent, {});
  }

  public openDownloadManagerConnectModal(): void {
    this._modalService.open(DownloadManagerConnectComponent, {});
  }

  public openLimitExceededModal(fromCreateMigrate?: boolean): void {
    let limitExceededType: LimitExceededMsgType = 'paid';

    if (this._userSubscription && this._userSubscription.newPlan) {
      limitExceededType = 'business-ecommerce';
    }
      const modal = this._modalService.open(LimitExceededComponent, {});
      modal.componentInstance.limitExceededType = limitExceededType;
      modal.componentInstance.userSubscription = this._userSubscription;
      modal.componentInstance.fromCreationCard = false;

  }

  private openProPlansUpgradeModal(): void {
    this._modalService.open(UpgradeTenWebProComponent, {});
  }

  private showOneMonthFreeModal(label): void {
    const analyticsData = {
      eventCategory: this.demoUser ? 'Free Upgrade Offer' : 'Free trial paywalls',
      eventAction: "Locked button click",
      eventLabel: label
    };
    if (this.demoUser) {
      const firstTime = TypeUtils.isNull(localStorage.getItem('dbShowFirstTime'));
      const options = {
        overflowHidden: false,
        backdropClass: 'blurred',
      };
      if (!firstTime) {
        options['width'] = 1240;
      } else {
        options['width'] = 460;
        options['height'] = 600;
      }
      const modal = this._modalService.open(OneWeekFreeDemoUpgradeComponent, options);
      modal.componentInstance.showFirstTime = firstTime;
    } else {
      this._modalService.open(PricingPageViewComponent, {overflowHidden: true, height: 960, width: 1076});
    }
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }

  showAPIUpgradePopup() {
    const options = {
      overflowHidden: false,
      backdropClass: 'blurred',
    };
    options['width'] = 1240;
    const modal = this._modalService.open(ApiResellerProComponent, options);
    modal.componentInstance.currentSubscription = this._userSubscription;
    modal.componentInstance.user = this._user;
    modal.componentInstance.fromDialog = true;
  }
}
